import { useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import { useParams } from "react-router-dom";
import { useListProjects } from "~/api/folders/folders";
import { useCompleteFtue } from "~/api/organizations/organizations";
import {
  getGetCurrentUserQueryKey,
  useGetCurrentUser,
} from "~/api/users/users";
import { trackEvent } from "~/utils/analytics";
import logger from "~/utils/logger";

export type FtueCompletionMethod = "create_case" | "explore_demo" | "skip";

export const useFtue = () => {
  const queryClient = useQueryClient();
  const { organizationId } = useParams<{ organizationId: string }>();
  const { data: user, isLoading: isUserLoading } = useGetCurrentUser();
  const { mutateAsync: completeFtueMutation, isPending: isCompletingFtue } =
    useCompleteFtue();

  const { data: casesData, isLoading: isCasesLoading } = useListProjects(
    organizationId ?? "",
    { archived: false, limit: 50 },
    { query: { enabled: !!organizationId } }
  );

  const allCases = casesData?.projects || [];
  const demoCase = allCases.find((c) => c.isDemo);
  const hasRealCases = allCases.some((c) => !c.isDemo);

  const isLoading = isUserLoading || isCasesLoading;
  const shouldShowFtue =
    !!organizationId &&
    !isLoading &&
    user &&
    !user.hasCompletedFtue &&
    !hasRealCases;

  const completeFtue = useCallback(
    async (method: FtueCompletionMethod) => {
      try {
        await completeFtueMutation();
        trackEvent({
          eventName: "ftue_completed",
          properties: { method },
        });
        await queryClient.invalidateQueries({
          queryKey: getGetCurrentUserQueryKey(),
        });
      } catch (error) {
        logger.error("Failed to complete FTUE:", error);
      }
    },
    [completeFtueMutation, queryClient]
  );

  const trackFtueEvent = useCallback(
    (
      eventName:
        | "ftue_welcome_viewed"
        | "ftue_create_case_clicked"
        | "ftue_explore_demo_clicked"
        | "ftue_skipped"
        | "ftue_report_upload_started"
        | "ftue_report_upload_success"
        | "ftue_report_upload_skipped"
        | "ftue_template_build_completed"
        | "ftue_template_build_failed"
        | "ftue_preview_generated"
        | "ftue_preview_skipped"
        | "ftue_preview_continued"
    ) => {
      trackEvent({ eventName });
    },
    []
  );

  return {
    user,
    isLoading,
    shouldShowFtue,
    completeFtue,
    isCompletingFtue,
    trackFtueEvent,
    demoCase,
  };
};
